import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube } from "lucide-react";

const QUICK_LINKS = [
  { label: "Home", id: "home" },
  { label: "Products", id: "products" },
  { label: "About Us", id: "about" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

const SOCIALS = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Twitter, label: "Twitter" },
  { icon: Youtube, label: "YouTube" },
];

const Footer = () => {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer id="contact" className="bg-stone-900 text-white" data-testid="footer">
      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Col 1: Logo + Tagline */}
          <div>
            <div className="flex items-baseline gap-0.5 mb-3">
              <span className="text-3xl font-black text-orange-400">Munchh</span>
              <span className="text-3xl font-black text-red-400">ONN</span>
            </div>
            <p className="text-orange-300 italic font-medium text-sm mb-3">
              "Mood Banaye Mast!"
            </p>
            <p className="text-stone-400 text-sm leading-relaxed">
              India's trusted snacking partner for over 40 years. Bringing
              authentic flavors and joy in every crunch!
            </p>

            {/* Social icons */}
            <div className="flex gap-3 mt-6">
              {SOCIALS.map(({ icon: Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-orange-500 transition-colors duration-200"
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Quick Links */}
          <div>
            <h4 className="font-bold text-white mb-6 text-base">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map(({ label, id }) => (
                <li key={label}>
                  <button
                    onClick={() => scrollTo(id)}
                    className="text-stone-400 hover:text-orange-400 transition-colors duration-200 text-sm flex items-center gap-2 group"
                    data-testid={`footer-link-${id}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Contact Info */}
          <div>
            <h4 className="font-bold text-white mb-6 text-base">Contact Us</h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <Phone size={15} className="text-orange-400 mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+919771479798"
                  className="text-stone-400 hover:text-orange-400 transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  +91 977 147 9798
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={15} className="text-orange-400 mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@munchhonn.com"
                  className="text-stone-400 hover:text-orange-400 transition-colors text-sm"
                  data-testid="footer-email"
                >
                  info@munchhonn.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-orange-400 mt-1 flex-shrink-0" />
                <div className="text-stone-400 text-sm space-y-3">
                  <div>
                    <p className="text-stone-300 font-semibold text-xs uppercase tracking-wider mb-1">
                      Registered Office
                    </p>
                    <p className="leading-relaxed">
                      A-20, New Krishna Park, Najafgarh Road, Vikaspuri, New Delhi - 110018
                    </p>
                  </div>
                  <div>
                    <p className="text-stone-300 font-semibold text-xs uppercase tracking-wider mb-1">
                      Plant
                    </p>
                    <p className="leading-relaxed">
                      B-5 & A-2 EPIP Industrial Area, Hajipur, Vaishali, Bihar 844101
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Col 4: Google Maps */}
          <div>
            <h4 className="font-bold text-white mb-6 text-base">Find Us</h4>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-700">
              <iframe
                src="https://maps.google.com/maps?q=28.631446,77.075609&z=15&output=embed"
                width="100%"
                height="210"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MunchhoNN Registered Office Location"
                data-testid="google-maps-embed"
              />
            </div>
            <p className="text-stone-500 text-xs mt-2 text-center">
              A-20, New Krishna Park, Vikaspuri, New Delhi
            </p>
          </div>
        </div>
      </div>

      {/* Copyright strip */}
      <div className="border-t border-stone-800 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-500 text-sm">
            © {new Date().getFullYear()} MunchhoNN. All rights reserved.
          </p>
          <p className="text-stone-500 text-sm">Made with love in India</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
